import { FunctionComponent, memo, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { OrganisationTagType } from '../../../../services';
import {
  Filter,
  TabItem,
  Tabs,
  TagsPicker,
  optArrItem,
  useFilter,
  useTags,
} from '../../../../shared';
import { StorySettings } from '../CreateStoryModal';
import { StorySync } from '../StorySync';
import { AutoNotification } from './AutoNotification';
import {
  DisplayProps,
  useCreateStoryAccessFilter,
  useCreateStoryCardFilter,
  useCreateStoryDisplayFilter,
  useCreateStoryOtherFilter,
} from './Filters';
import { StoryWriteAccess } from './StoryWriteAccess';

import classes from './CreateStorySettings.module.scss';

export enum CreateStorySettingsTabType {
  DISPLAY,
  CARD,
  ACCESS,
  TAGS,
  OTHER,
  AUTO_NOTIFICATION,
  SYNC,
}

interface CreateStorySettingsProps {
  storyId?: number;
  settings: StorySettings;
  onChange: (settings: StorySettings) => void;
  isStandard: boolean;
  activeSettingsTabIndex: number;
  setActiveSettingsTabIndex: (index: number) => void;
}

export const CreateStorySettings: FunctionComponent<CreateStorySettingsProps> = memo(
  ({
    storyId,
    settings,
    onChange,
    isStandard,
    activeSettingsTabIndex,
    setActiveSettingsTabIndex,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'createStorySettings' });

    const { hasInternalTags, hasExternalTags, hasExternalStatusTags, hasContentTags } = useTags();

    const { getGroupItemValue } = useFilter();

    const { display, card, tags, access, other, autoNotification, sync, writeAccess } = settings;

    const { internal, setInternal, external, setExternal, externalStatus, setExternalStatus } =
      tags;

    const { userTags, setUserTags, users, setUsers } = writeAccess;

    const { groups: displayGroups } = useCreateStoryDisplayFilter(display);

    const { groups: cardGroups } = useCreateStoryCardFilter(card);

    const { groups: accessGroups } = useCreateStoryAccessFilter();

    const displayGroup = { filter: display, groupId: 'display' };

    const showFeedFollowBehavior = Boolean(
      getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInWebappNewsFeed }) ||
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInNewsFeed })
    );

    const { groups: otherGroups } = useCreateStoryOtherFilter({
      filter: other,
      showFeedFollowBehavior,
    });

    const showTags = useMemo(
      () => Boolean(isStandard && hasContentTags),
      [hasContentTags, isStandard]
    );

    const tabItems: TabItem[] = useMemo(
      () => [
        {
          index: CreateStorySettingsTabType.DISPLAY,
          name: t('createStorySettingsTabs.display'),
          content: (
            <Filter
              filter={display}
              groups={displayGroups}
              onChange={(display) => onChange({ ...settings, display })}
            />
          ),
        },
        ...optArrItem(isStandard, {
          index: CreateStorySettingsTabType.CARD,
          name: t('createStorySettingsTabs.card'),
          content: (
            <Filter
              filter={card}
              groups={cardGroups}
              onChange={(card) => onChange({ ...settings, card })}
            />
          ),
        }),
        ...optArrItem(showTags, {
          index: CreateStorySettingsTabType.TAGS,
          name: t('createStorySettingsTabs.tags'),
          content: (
            <div className={classes['create-story-settings__tags']}>
              {hasInternalTags && (
                <TagsPicker
                  tags={internal}
                  setTags={setInternal}
                  type={OrganisationTagType.INTERNAL}
                />
              )}
              {hasExternalTags && (
                <TagsPicker
                  tags={external}
                  setTags={setExternal}
                  type={OrganisationTagType.EXTERNAL}
                />
              )}
              {hasExternalStatusTags && (
                <TagsPicker
                  tags={externalStatus}
                  setTags={setExternalStatus}
                  type={OrganisationTagType.EXTERNAL_STATUS}
                  singleSelect
                  closeOnSelect
                />
              )}
            </div>
          ),
        }),
        ...optArrItem(isStandard, {
          index: CreateStorySettingsTabType.ACCESS,
          name: t('createStorySettingsTabs.access'),
          content: (
            <div className={classes['create-story-settings__access']}>
              <Filter
                filter={access}
                groups={accessGroups}
                onChange={(access) => onChange({ ...settings, access })}
              />

              <div className={classes['create-story-settings__divider']} />

              <StoryWriteAccess
                userTags={userTags}
                setUserTags={setUserTags}
                users={users}
                setUsers={setUsers}
              />
            </div>
          ),
        }),
        ...optArrItem(isStandard, {
          index: CreateStorySettingsTabType.OTHER,
          name: t('createStorySettingsTabs.other'),
          content: (
            <>
              <Filter
                filter={other}
                groups={otherGroups}
                onChange={(other) => onChange({ ...settings, other })}
              />

              {!showFeedFollowBehavior && (
                <div className={classes['create-story-settings__hint']}>
                  {ctxT('feed-follow-behavior-news-feed-hint')}
                </div>
              )}
            </>
          ),
        }),
        ...optArrItem(isStandard, {
          index: CreateStorySettingsTabType.AUTO_NOTIFICATION,
          name: t('createStorySettingsTabs.auto-notification'),
          content: <AutoNotification {...autoNotification} />,
        }),
        ...optArrItem(isStandard, {
          index: CreateStorySettingsTabType.SYNC,
          name: t('createStorySettingsTabs.sync'),
          content: (
            <div className={classes['create-story-settings__sync']}>
              <StorySync {...sync} storyId={storyId} withToggle />
            </div>
          ),
        }),
      ],
      [
        access,
        accessGroups,
        autoNotification,
        card,
        cardGroups,
        ctxT,
        display,
        displayGroups,
        external,
        externalStatus,
        hasExternalStatusTags,
        hasExternalTags,
        hasInternalTags,
        internal,
        isStandard,
        onChange,
        other,
        otherGroups,
        setExternal,
        setExternalStatus,
        setInternal,
        setUserTags,
        setUsers,
        settings,
        showFeedFollowBehavior,
        showTags,
        storyId,
        sync,
        t,
        userTags,
        users,
      ]
    );

    return (
      <div className={classes['create-story-settings']}>
        <Tabs
          items={tabItems}
          selectedTabIndex={activeSettingsTabIndex}
          setSelectedTabIndex={setActiveSettingsTabIndex}
          scrollable={isMobileOnly}
          noBackground
        />
      </div>
    );
  }
);
